// Task modal — centered dialog (createPortal, scrim, Esc/scrim-click close).
// Two modes:
//   • create: no `taskId`, submit builds the task
//   • edit:   `taskId` supplied → load, patch inline as fields change
//
// Wave N MVP — comments + checklist + activity log all live inside here.
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  X, Calendar, User as UserIcon, Users as UsersIcon, Tag as TagIcon,
  MessageSquare, ClipboardCheck, Trash2, Send,
  Loader2, Plus, ListChecks, Repeat, Bell, BellOff,
} from "lucide-react";
import { TYPE_META, TypePills, StatusPills, PriorityPills, EffortPills, Switch, VoiceFill } from "./TaskDrawerHead";
import TaskImages, { useImagePaste } from "./TaskImages";
import { workspaceApi, STATUS_TONE, withMe, dueInfo, pinnedLink, isMgmt, checkedInThisWeek, emitTasksChanged } from "../_shared";
import { TeamModePicker, EachMemberProgress } from "./TaskRhythm";
import { RepeatPicker, FilesLinks, Timeline, CompleteBar } from "./TaskDrawerParts";
import { LinkButton } from "./TaskBits";
import RoutinePanel from "./RoutinePanel";
import { useAuth } from "../../../context/AuthContext";

const PRIORITIES = [
  { key: "low",    label: "Low"    },
  { key: "medium", label: "Medium" },
  { key: "high",   label: "High"   },
  { key: "urgent", label: "Urgent" },
];

export default function TaskDrawer({
  open,
  taskId,
  createDefaults = {},   // seed for create mode: {team_id, assignee_id, linked_entities…}
  users,
  teams,
  onClose,
  onSaved,               // called with the saved task doc
}) {
  const { user } = useAuth();
  const meKey = user?.id || user?._id || user?.email || "me";
  const isEdit = !!taskId;

  const [cfg, setCfg] = useState(null);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [newChecklistText, setNewChecklistText] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState({});
  const dirty = Object.keys(pending).length > 0;
  const tryClose = () => { if (dirty && !window.confirm("You have unsaved changes. Discard them?")) return; setPending({}); onClose?.(); };

  useEffect(() => {
    if (!open) return;
    workspaceApi.getConfig().then(setCfg).catch(() => setCfg(null));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") tryClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose, dirty]);

  useEffect(() => {
    if (!open) { setTask(null); setPending({}); return; }
    setError(""); setPending({});
    if (isEdit) {
      setLoading(true);
      workspaceApi.getTask(taskId)
        .then((r) => setTask(r.task))
        .catch(() => toast.error("Failed to load task"))
        .finally(() => setLoading(false));
    } else {
      setTask({
        title: "", description: "", type: "task", status: null, links: [], attachments: [], ack_required: false,
        recurrence: { rule: "none", mode: "on_complete", days: [1], month_day: 1 },
        priority: "medium", assignee_id: null, team_id: null,
        watcher_ids: [], due_date: null, recurrence_class: "none", guide: false, assign_mode: "shared",
        tags: [], linked_entities: [], checklist: [], custom_fields: {},
        ...createDefaults,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  const set = (patch) => setTask((t) => ({ ...t, ...patch }));
  const [statusAsk, setStatusAsk] = useState(false);
  const img = useImagePaste(isEdit ? taskId : null, (att) => setTask((t) => ({ ...t, attachments: [...(t?.attachments || []), att] })));

  // Create mode: once the config lands, snap `type`/`status` onto valid keys so a
  // create can never 400 with "unknown type/status" (the silent-failure the user hit
  // on production, where the config catalog differs from the hardcoded defaults).
  useEffect(() => {
    if (!open || isEdit || !cfg) return;
    setTask((t) => {
      if (!t) return t;
      const types = cfg.types || [];
      const statuses = cfg.statuses || [];
      const nextType = types.some((x) => x.key === t.type) ? t.type : (types[0]?.key || t.type);
      const nextStatus = t.status || statuses[0]?.key || null;
      if (nextType === t.type && nextStatus === t.status) return t;
      return { ...t, type: nextType, status: nextStatus };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, open, isEdit]);

  // Edit mode: changes are staged locally — nothing is written until "Save changes".
  const canEditTask = !isEdit || isMgmt(user) || task?.created_by === meKey || task?.assignee_id === meKey || task?.reviewer_id === meKey;
  const persistPatch = (patch) => {
    if (!canEditTask) { toast("View only — claim it or ask the owner / creator to change this task."); return; }
    set(patch);
    if (isEdit) setPending((p) => ({ ...p, ...patch }));
  };
  const requestSave = () => { if (isEdit && dirty && !("status" in pending)) { setStatusAsk(true); return null; } return saveChanges(); };
  const saveChanges = async (extra = {}) => {
    if (!dirty && !Object.keys(extra).length) return task;
    setStatusAsk(false);
    setSaving(true);
    try {
      const r = await workspaceApi.patchTask(taskId, { ...pending, ...extra });
      setTask(r.task); setPending({});
      onSaved?.(r.task); emitTasksChanged();
      toast.success("Changes saved", { description: task?.ref });
      return r.task;
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || "" });
      return null;
    } finally { setSaving(false); }
  };
  const discard = async () => {
    setPending({});
    try { const r = await workspaceApi.getTask(taskId); setTask(r.task); } catch { /* ignore */ }
    toast("Changes discarded");
  };

  const doCreate = async () => {
    setError("");
    if (!task?.title?.trim()) {
      setError("Title is required — give the task a name before creating it.");
      document.querySelector("[data-testid='task-drawer-title']")?.focus();
      return;
    }
    if (!cfg || !(cfg.statuses || []).length || !(cfg.types || []).length) {
      setError("Workspace configuration didn't load. Click Refresh and try again.");
      return;
    }
    setSaving(true);
    try {
      const r = await workspaceApi.createTask(task);
      toast.success(`Task created: ${r.task.title}`);
      onSaved?.(r.task);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "string" && detail)
        || (Array.isArray(detail) && detail.map((d) => d.msg).join("; "))
        || e?.message || "Create failed — please try again.";
      console.error("[TaskDrawer] create failed:", e?.response?.status, detail || e);
      setError(msg);
      toast.error("Create failed", { description: msg });
    } finally { setSaving(false); }
  };

  const claim = async () => {
    setSaving(true);
    try {
      const r = await workspaceApi.claimTask(taskId);
      setTask(r.task);
      onSaved?.(r.task);
      toast.success("Claimed");
    } catch (e) { toast.error("Claim failed", { description: e?.response?.data?.detail || "" }); }
    finally { setSaving(false); }
  };

  const sendComment = async () => {
    const text = commentText.trim();
    if (!text || !isEdit) return;
    try {
      await workspaceApi.addComment(taskId, text);
      setCommentText("");
      const r = await workspaceApi.getTask(taskId);
      setTask(r.task);
    } catch { toast.error("Comment failed"); }
  };

  const addChecklistItem = () => {
    const text = newChecklistText.trim();
    if (!text) return;
    const next = [...(task.checklist || []), { text, done: false }];
    persistPatch({ checklist: next });
    setNewChecklistText("");
  };
  const toggleChecklist = (i) => {
    const next = task.checklist.map((c, idx) => idx === i ? { ...c, done: !c.done } : c);
    persistPatch({ checklist: next });
  };
  const removeChecklist = (i) => {
    persistPatch({ checklist: task.checklist.filter((_, idx) => idx !== i) });
  };

  const statuses = cfg?.statuses || [];
  const types    = cfg?.types    || [];

  if (!open) return null;
  const statusTone = STATUS_TONE[statuses.find((s) => s.key === task?.status)?.color] || STATUS_TONE.slate;

  const tm = TYPE_META[task?.type] || TYPE_META.task;
  const TIcon = tm.icon;
  const team = (teams || []).find((t) => t.id === task?.team_id);
  const ownerName = withMe(users, user).find((u) => u.id === task?.assignee_id)?.name;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      onClick={tryClose}
      data-testid="task-drawer-overlay"
    >
      <div className="absolute inset-0 bg-[#02060d]/75 backdrop-blur-md" data-testid="task-modal-scrim" />
      <div
        className="ws-modal relative w-full max-w-[1480px] h-[94vh] lg:h-[90vh] border border-white/10 rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,.8)] overflow-y-auto lg:overflow-hidden flex flex-col"
        style={{ background: "linear-gradient(180deg,#0d1726 0%,#0a1220 40%,#081019 100%)" }}
        onClick={(e) => e.stopPropagation()}
        onPaste={isEdit ? img.onPaste : undefined}
        data-testid="task-drawer"
      >
        <div className="h-1 rounded-t-2xl shrink-0" style={{ background: `linear-gradient(90deg, ${tm.hex}, #2fc8cb 60%, #f59e0b)` }} />
        {/* Header */}
        <div className="sm:sticky top-0 z-10 backdrop-blur-xl border-b border-white/[0.07] px-4 sm:px-6 pt-4 pb-3" style={{ background: "rgba(10,18,32,.92)" }}>
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-xl hidden sm:grid place-items-center shrink-0 border" style={{ background: `${tm.hex}1f`, borderColor: `${tm.hex}66`, color: tm.hex }}>
              <TIcon className="w-5 h-5" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap text-[10.5px] font-mono uppercase tracking-widest">
                <span className="text-slate-400" data-testid="task-drawer-ref">{isEdit ? (task?.ref || "Task") : "New task"}</span>
                <span style={{ color: tm.hex }}>{tm.label}</span>
                {task?.status && (
                  <span className={`px-1.5 py-0.5 rounded border text-[9.5px] ${statusTone.bg} ${statusTone.text} ${statusTone.border}`} data-testid="task-drawer-status-chip">
                    {statuses.find((s) => s.key === task.status)?.label || task.status}
                  </span>
                )}
                {task?.due_date && <span className={dueInfo(task).tone === "red" ? "text-rose-300" : "text-slate-500"}>{dueInfo(task).text}</span>}
                {task?.quiet && <span className="px-1.5 py-0.5 rounded border border-slate-400/40 text-slate-300 text-[9.5px]" title={`Quiet — set by ${task.quiet_by || "Management"}`} data-testid="task-drawer-quiet-badge">QUIET</span>}
                {isEdit && task && isMgmt(user) && <button type="button" onClick={async () => { try { const r = await workspaceApi.setQuiet(task.id, !task.quiet); setTask(r.task); onSaved?.(r.task); toast.success(r.task.quiet ? "Quiet — no alerts, ticks or Do next for this task" : "Back to normal alerts"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } }}
                  className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded border border-white/15 text-slate-300 hover:text-white hover:border-white/30 text-[10px] normal-case tracking-normal" data-testid="task-drawer-quiet-toggle">
                  {task.quiet ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}{task.quiet ? "Unquiet" : "Make quiet"}</button>}
              </div>
              <textarea rows={1}
                value={task?.title || ""}
                ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } }}
                onChange={(e) => { set({ title: e.target.value.replace(/\n/g, " ") }); if (error) setError(""); }}
                onBlur={() => isEdit && task?.title && persistPatch({ title: task.title })}
                placeholder="What needs doing?"
                className={`w-full mt-1 resize-none overflow-hidden leading-snug bg-transparent text-lg sm:text-xl font-semibold text-white focus:outline-none placeholder:text-slate-600 ${error && !task?.title?.trim() ? "ring-1 ring-rose-500/60 rounded px-1 -mx-1" : ""}`}
                data-testid="task-drawer-title"
              />
              <div className="flex items-center gap-3 mt-1 text-[11.5px] text-slate-400">
                <span className="inline-flex items-center gap-1"><UsersIcon className="w-3 h-3 text-teal-300" />{team?.name || "Personal"}</span>
                <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3 text-amber-300" />{task?.assign_mode === "each" ? "Every member" : (ownerName || (task?.team_id ? "Pool — claimable" : "Unassigned"))}</span>
                {(task?.linked_entities || []).filter((e) => e.type === "person").map((e) => <span key={e.id} className="inline-flex items-center gap-1 text-teal-300" data-testid={`task-linked-person-${e.id}`}>Linked · {e.label || "customer"}</span>)}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2 empty:hidden">
            {isEdit && task?.ack_required && !task?.routine && (
                <button onClick={async () => { const r = await workspaceApi.checkin(taskId, !checkedInThisWeek(task, meKey)); setTask(r.task); emitTasksChanged(); }}
                  className={`px-2.5 py-1.5 rounded-lg border text-[11px] ${checkedInThisWeek(task, meKey) ? "border-teal-400/60 bg-teal-500/15 text-teal-200" : "border-white/15 text-slate-300 hover:border-teal-400/50"}`} data-testid="task-drawer-checkin">
                  {checkedInThisWeek(task, meKey) ? "✓ On it this week" : "I'm on this"}
                </button>
              )}
              {pinnedLink(task) && <LinkButton link={pinnedLink(task)} label testid="task-drawer-pinned-link" />}
              </div>
            </div>
            {dirty && <span className="self-center px-2 py-1 rounded-md border border-amber-400/50 bg-amber-500/10 text-amber-200 text-[11px] font-semibold" data-testid="task-drawer-unsaved">Unsaved changes</span>}
            <button onClick={tryClose} className="w-8 h-8 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5" data-testid="task-drawer-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading && (
          <div className="p-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline-block mr-1" /> Loading…</div>
        )}

        {!loading && task && (
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 lg:divide-x divide-white/[0.06]">
          <div className={`px-4 sm:px-6 py-5 space-y-5 lg:overflow-y-auto ${canEditTask ? "" : "[&_section]:opacity-60 [&_select]:pointer-events-none [&_input]:pointer-events-none [&_button]:cursor-not-allowed"}`} data-testid="task-drawer-left">
            {!canEditTask && <div className="rounded-lg border border-sky-400/40 bg-sky-500/10 px-3 py-2 text-[12.5px] text-sky-100" data-testid="task-drawer-readonly">View only — you can comment{task.team_id && !task.assignee_id ? " or claim it" : ""}. Only the owner, creator, reviewer or Management can change this task.</div>}
            {isEdit && (
              <div className={`rounded-xl border-2 p-3 transition-colors ${"status" in pending ? "border-emerald-400/50 bg-emerald-500/[0.06]" : "border-amber-400/50 bg-amber-500/[0.07]"}`} data-testid="task-drawer-status-panel">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[13px] font-semibold text-white">Status</span>
                  <span className="text-[11px] text-amber-200/90">{"status" in pending ? "Updated — save to keep it" : "Keep this up to date — you'll be asked before saving"}</span>
                </div>
                <StatusPills statuses={task?.routine ? statuses.filter((s) => !["done", "cancelled"].includes(s.key)) : statuses} value={task.status || statuses[0]?.key} onChange={(v) => persistPatch({ status: v })} />
              </div>
            )}
            <VoiceFill task={task} types={types} onApply={persistPatch} />

            <Section title="What kind of task" icon={ListChecks} tone="text-sky-300" testid="task-drawer-kind">
              <TypePills types={types} value={task.type} onChange={(v) => persistPatch({ type: v })} />
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-3">
                {!isEdit && <Field label="Status"><StatusPills statuses={task?.routine ? statuses.filter((s) => !["done", "cancelled"].includes(s.key)) : statuses} value={task.status || statuses[0]?.key} onChange={(v) => persistPatch({ status: v })} /></Field>}
                <Field label="Priority"><PriorityPills value={task.priority} onChange={(v) => persistPatch({ priority: v })} /></Field>
              </div>
            </Section>

            <Section title="Who & when" icon={UsersIcon} tone="text-teal-300" testid="task-drawer-who">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={<><UsersIcon className="w-3 h-3 inline-block" /> Team</>}>
                  <select value={task.team_id || ""} onChange={(e) => persistPatch({ team_id: e.target.value || null, ...(!e.target.value && { assign_mode: "shared" }) })}
                    className="ws-input" data-testid="task-drawer-team">
                    <option value="">— Personal —</option>
                    {(teams || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </Field>
                <Field label={<><UserIcon className="w-3 h-3 inline-block" /> Owner</>}>
                  {task.team_id && task.assign_mode === "each" ? (
                    <div className="ws-input text-teal-200" data-testid="task-drawer-assignee-each">Every team member</div>
                  ) : (
                    <select value={task.assignee_id || ""} onChange={(e) => persistPatch({ assignee_id: e.target.value || null })}
                      className="ws-input" data-testid="task-drawer-assignee">
                      <option value="">{task.team_id ? "— Pool (anyone in the team can claim) —" : "— Unassigned —"}</option>
                      {withMe(users, user).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                    </select>
                  )}
                </Field>
                <Field label={<><Calendar className="w-3 h-3 inline-block" /> Due</>}>
                  <div className="flex gap-1.5">
                    <input type="date" value={(task.due_date || "").slice(0, 10)}
                      onChange={(e) => persistPatch({ due_date: e.target.value || null })}
                      className="ws-input" data-testid="task-drawer-due" />
                    <input type="time" value={task.due_time || ""} onChange={(e) => persistPatch({ due_time: e.target.value || null })}
                      className="ws-input max-w-[110px]" data-testid="task-drawer-due-time" />
                  </div>
                  {task.due_date && dueInfo(task).tone === "red" && <div className="text-[11px] text-rose-300 mt-0.5" data-testid="task-drawer-overdue">{dueInfo(task).text.replace(/^1d overdue$/, "1 day overdue").replace("d overdue", " days overdue")}</div>}
                </Field>
                {["approval", "report"].includes(task.type) ? (
                  <Field label={`Reviewer${task.type === "approval" ? " (required)" : ""}`}>
                    <select value={task.reviewer_id || ""} onChange={(e) => persistPatch({ reviewer_id: e.target.value || null })} className="ws-input" data-testid="task-drawer-reviewer">
                      <option value="">— None —</option>
                      {withMe(users, user).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                    </select>
                  </Field>
                ) : (
                  <Field label={<><TagIcon className="w-3 h-3 inline-block" /> Tags</>}>
                    <input value={(task.tags || []).join(", ")}
                      onChange={(e) => set({ tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                      onBlur={() => isEdit && persistPatch({ tags: task.tags })}
                      placeholder="comma, separated" className="ws-input" data-testid="task-drawer-tags" />
                  </Field>
                )}
                {task.status === "waiting" && (
                  <Field label="Waiting on">
                    <input value={task.waiting_on?.label || ""} onChange={(e) => set({ waiting_on: { kind: "other", label: e.target.value } })}
                      onBlur={() => isEdit && persistPatch({ waiting_on: task.waiting_on })} placeholder="Who / what?" className="ws-input" data-testid="task-drawer-waiting-on" />
                  </Field>
                )}
                <Field label="Effort"><EffortPills value={task.effort} onChange={(v) => persistPatch({ effort: v })} /></Field>
                <div className="flex items-end">
                  {task.ack_required && !task.routine && <Switch on onChange={(v) => persistPatch({ ack_required: v })} label="Weekly check-in (old)" sub="Replaced by weekly routines — switch off, then set Repeat: Weekly" testid="task-drawer-ack-toggle" />}
                </div>
              </div>
            </Section>

            {isEdit && task.routine && <RoutinePanel task={task} onTicked={(t) => { setTask(t); onSaved?.(t); }} />}
            <Section title="Rhythm" icon={Repeat} tone="text-violet-300" testid="task-drawer-rhythm">
            <RepeatPicker task={task} onChange={persistPatch} />
            {task.team_id && !task.routine && (teams || []).some((t) => t.id === task.team_id) && <div className="mt-3"><TeamModePicker task={task} onChange={persistPatch} /></div>}
            {task.team_id && task.routine && <div className="mt-2 text-[11.5px] text-slate-400" data-testid="task-routine-team-hint">Team routine: anyone in the team ticks each slot once — everyone sees who did it.</div>}
            </Section>
            {isEdit && task.team_id && task.assign_mode === "each" && (
              <EachMemberProgress task={task} team={(teams || []).find((t) => t.id === task.team_id)} users={users}
                meKey={meKey} onTicked={(t) => { setTask(t); onSaved?.(t); }} />
            )}

          </div>
          <div className="px-4 sm:px-6 py-5 space-y-5 lg:overflow-y-auto bg-black/[0.12]" data-testid="task-drawer-right">
            {/* Description */}
            <Field label="Description — what needs to happen">
              <textarea rows={5} value={task.description || ""}
                onChange={(e) => persistPatch({ description: e.target.value })}
                placeholder="What needs to happen?"
                className="ws-input"
                data-testid="task-drawer-description"
              />
            </Field>

            {/* Claim if unassigned */}
            {isEdit && !task.assignee_id && task.team_id && task.assign_mode !== "each" && !task.guide && (
              <button onClick={claim} disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 font-mono uppercase tracking-widest text-[11px] hover:bg-amber-500/20"
                data-testid="task-drawer-claim">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserIcon className="w-3 h-3" />} Claim this task
              </button>
            )}

            {/* Checklist */}
            <Section title={`Checklist${(task.checklist || []).length ? ` · ${(task.checklist || []).filter((c) => c.done).length}/${task.checklist.length}` : ""}`} icon={ClipboardCheck} tone="text-emerald-300" testid="task-drawer-checklist">
              {(task.checklist || []).length > 0 && (
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-2"><div className="h-full bg-gradient-to-r from-teal-400 to-emerald-400 transition-[width]" style={{ width: `${Math.round(100 * (task.checklist || []).filter((c) => c.done).length / task.checklist.length)}%` }} /></div>
              )}
              {(task.checklist || []).map((c, i) => (
                <label key={i} className="flex items-center gap-2 py-1 group" data-testid={`task-drawer-check-${i}`}>
                  <input type="checkbox" checked={!!c.done} onChange={() => toggleChecklist(i)} className="accent-emerald-500 w-4 h-4" />
                  <span className={`flex-1 text-[13px] ${c.done ? "text-slate-500 line-through" : "text-slate-200"}`}>{c.text}</span>
                  <button onClick={() => removeChecklist(i)} className="opacity-0 group-hover:opacity-100 text-rose-400 hover:text-rose-300"><Trash2 className="w-3 h-3" /></button>
                </label>
              ))}
              <div className="flex items-center gap-2 mt-1">
                <input value={newChecklistText} onChange={(e) => setNewChecklistText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChecklistItem(); } }}
                  placeholder="Add item…" className="ws-input flex-1 text-[12.5px]"
                  data-testid="task-drawer-check-new" />
                <button onClick={addChecklistItem} className="p-1.5 rounded border border-white/10 text-slate-400 hover:text-white hover:bg-white/5" data-testid="task-drawer-check-add">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </Section>

            <FilesLinks task={task} isEdit={isEdit} onPatch={persistPatch} />

            <Section title={`Screenshots${(task.attachments || []).filter((a) => a.kind === "image").length ? ` · ${(task.attachments || []).filter((a) => a.kind === "image").length}` : ""}`} icon={ClipboardCheck} tone="text-sky-300" testid="task-drawer-screenshots">
              {isEdit ? <TaskImages taskId={taskId} attachments={task.attachments || []} busy={img.busy} upload={img.upload}
                onRemoved={(id) => setTask((t) => ({ ...t, attachments: (t.attachments || []).filter((a) => a.id !== id) }))} />
                : <div className="text-[11.5px] text-slate-500">Create the task first, then paste screenshots here.</div>}
            </Section>

            {isEdit && (
              <>
                {/* Comments */}
                <Section title="Comments · post instantly" icon={MessageSquare} tone="text-amber-300" testid="task-drawer-comments">
                  <div className="flex items-end gap-2 mt-2">
                    <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendComment(); } }}
                      rows={2} placeholder="Add a comment… (Cmd/Ctrl-Enter to send)"
                      className="ws-input flex-1 text-[12.5px]"
                      data-testid="task-drawer-comment-input" />
                    <button onClick={sendComment} className="p-2 rounded border border-white/10 text-slate-400 hover:text-white hover:bg-white/5" data-testid="task-drawer-comment-send">
                      <Send className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </Section>

                <Timeline task={task} users={users} statuses={statuses} />
              </>
            )}
          </div>
          </div>
        )}

        {isEdit && task && <CompleteBar key={task.status} task={task} cfg={cfg} canEdit={isMgmt(user) || task.created_by === meKey || task.assignee_id === meKey || task.reviewer_id === meKey}
          dirty={dirty} saving={saving} onSave={requestSave} onDiscard={discard}
          onDone={(t) => { setTask(t); setPending({}); onSaved?.(t); if (t?.status === "done") onClose?.(); }} />}
        {statusAsk && task && (
          <div className="absolute inset-0 z-30 grid place-items-center bg-black/60 rounded-2xl" data-testid="task-status-prompt">
            <div className="w-[min(560px,92%)] rounded-2xl border border-amber-400/40 bg-[#0d1726] p-5 shadow-2xl">
              <div className="text-[15px] font-semibold text-white">Before you save — what's the status now?</div>
              <p className="text-[12px] text-slate-400 mt-1">You changed this task but not its status. Pick the current status, or confirm it's still <b className="text-slate-200">{statuses.find((x) => x.key === task.status)?.label || task.status}</b>.</p>
              <div className="mt-3"><StatusPills statuses={task?.routine ? statuses.filter((s) => !["done", "cancelled"].includes(s.key)) : statuses} value={task.status} onChange={(v) => { if (v !== task.status) { set({ status: v }); saveChanges({ status: v }); } }} /></div>
              <div className="flex items-center gap-2 mt-4">
                <button onClick={() => setStatusAsk(false)} className="px-3 py-1.5 rounded-lg text-[12px] text-slate-400 hover:text-white" data-testid="task-status-prompt-back">Back</button>
                <button onClick={() => saveChanges()} className="ml-auto px-3 py-1.5 rounded-lg border border-white/15 text-[12px] text-slate-100 hover:bg-white/5" data-testid="task-status-prompt-same">Still the same — save</button>
              </div>
            </div>
          </div>
        )}
        {/* Footer — create only */}
        {!isEdit && (
          <div className="sticky bottom-0 border-t border-white/[0.07] rounded-b-2xl px-6 py-3 flex flex-col gap-2" style={{ background: "rgba(8,16,25,.96)" }}>
            {error && (
              <div className="flex items-start gap-2 text-[12px] text-rose-200 bg-rose-500/10 border border-rose-500/30 rounded px-2.5 py-1.5" data-testid="task-drawer-error" role="alert">
                <span className="font-mono">⚠</span><span>{error}</span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-1.5 rounded text-slate-400 hover:text-white text-[12px] font-mono" data-testid="task-drawer-cancel">Cancel</button>
              <button onClick={doCreate} disabled={saving}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 hover:brightness-110 disabled:opacity-60 text-slate-900 font-semibold text-[12.5px] shadow-[0_6px_20px_-6px_rgba(245,158,11,.6)]"
                data-testid="task-drawer-create">
                {saving ? "Creating…" : "Create task"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function Field({ label, children }) {
  return (
    <div className="block">
      <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mb-1 flex items-center gap-1">{label}</div>
      {children}
    </div>
  );
}
function Section({ title, icon: Icon, tone = "text-slate-400", testid, children }) {
  return (
    <section className="rounded-xl border border-white/[0.07] p-4 bg-white/[0.02] hover:border-white/[0.12] transition-colors" data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-widest text-slate-300 font-mono mb-3 flex items-center gap-1.5">
        <Icon className={`w-3.5 h-3.5 ${tone}`} /> {title}
      </div>
      {children}
    </section>
  );
}
